<template>
    <div class="f fv dw-10 ath-table bf br-m"
        :class="[restrictHeight ? 'oh' : '']">
        <div class="ath-table-body-container"
            :class="[restrictHeight ? 'restricted-height' : '']">
            <div class="f fv dw-10 ath-table-body"
                :class="[
                    isLoading ? 'el-stencil--loading' : '',
                    lockFirstColumn ? 'ath-table-lock-first' : ''
                ]">
                <!-- Table Super Headers -->
                <AthTableSuperHeader :superHeaders="uiSuperHeaders"
                    :class="[hasSuperHeaders ? 'round-top-corners' : '']" />
                <!-- Table Headers -->
                <AthTableHeader :columns="columns"
                    :superHeaders="uiSuperHeaders"
                    :sortUid="uid"
                    :class="[!hasSuperHeaders ? 'round-top-corners' : '']" />
                <!-- Empty -->
                <AthEmpty v-if="!isLoading && sorted.length === 0" v-bind="emptyProps" />
                <!-- List -->
                <template v-else-if="!isLoading">
                    <!-- Pinned unsorted data -->
                    <AthLazy v-for="obj in rowsPinned" :key="obj.uid" class="ath-table-lazy">
                        <AthTableRowContainer :cname="uiRowCname"
                            :data="obj"
                            :columns="columns"
                            @onDropdownItemClicked="onDropdownItemClicked"
                            @onCheckboxChange="onCheckboxChange"
                            @onButtonClicked="onButtonClicked"
                            @click="onRowClicked(obj)" />
                    </AthLazy>
                    <!-- Standard sorted data -->
                    <AthLazy v-for="(obj, index) in rows" :key="obj.uid" class="ath-table-lazy">
                        <AthTableRowContainer :idx="index"
                            :cname="uiRowCname"
                            :data="obj"
                            :columns="columns"
                            :allowRedirect="allowRedirect"
                            :class="[index === rows.length - 1 ? 'round-bottom-corners' : '']"
                            @onDropdownItemClicked="onDropdownItemClicked"
                            @onCheckboxChange="onCheckboxChange"
                            @onButtonClicked="onButtonClicked"
                            @click="onRowClicked"
                            @clickColumn="onClickColumn"
                            @redirect="onRedirect" />
                    </AthLazy>
                </template>
                <!-- Stencil -->
                <template v-else>
                    <AthTableRowStencil v-for="i in 6" :columns="columns" :key="i" :index="i" />
                </template>
            </div>
        </div>
    </div>
</template>
<script>
import { guuid, isNeArray, isNeString, isObject } from '@aretheyhappy/utils';
import UIMobileMixin from '../../../mixins/UIMobile';
import AthEmpty from '../../atoms/Empty.vue';
import AthLazy from '../../atoms/Lazy.vue';
import AthTableHeader from './TableHeader.vue';
import AthTableRowContainer from './TableRowContainer.vue';
import AthTableRowStencil from './TableRowStencil.vue';
import AthTableSuperHeader from './TableSuperHeader.vue';

export default {
    name: 'mead-table',
    props: {
        /**
         * uid of parent component, used to send sort events
         */
        pubkey      : {type: [Boolean, String], default: () => false},
        /**
         * If data is current loading in parent component
         */
        isLoading   : {type: Boolean, default: () => false},
        /**
         * If the first column should be locked to the left when scrolling
         */
        lockFirstColumn: {type: Boolean, default: () => false},
        /**
         * If interactive events should be used
         */
        uiInteractive: {type: Boolean, default: () => true},
        /**
         * field configuration for each column. Should be an array of TableFields
         */
        fields      : {type: Array, default: () => []},
        /**
         * Super header configuration. Should be an array of TableSuperHeaders
         */
        superHeaders: {type: Array, default: () => []},
        /**
         * Row data for the table
         */
        data        : {type: Array, default: () => []},
        /**
         * Pinned row data for the table
         */
        pinnedData  : {type: Array, default: () => []},
        /**
         * If the redirect arrow at the end of the row should be visible
         */
        allowRedirect: {type: Boolean, default: () => false},
        /**
         * Props for the empty component
         */
        emptyProps: {type: Object, default: () => {}},
        /**
         * Whether the table body's height is constrained with internal scrolling
         * (max-height with overflow). When set to false, the table expands to fit all
         * rows without clipping its overflow, which keeps dropdown menus in the last
         * couple of rows from being cut off.
         */
        restrictHeight: {type: Boolean, default: () => true},
    },
    mixins: [UIMobileMixin],
    components: {
        AthTableRowContainer,
        AthTableRowStencil,
        AthTableHeader,
        AthTableSuperHeader,
        AthEmpty,
        AthLazy,
    },
    data () {
        return {
            uid   : guuid(),
            sorted: [],
        };
    },
    methods: {
        sortRetrieve: function (next) {
            if (!isNeArray(this.data) || this.externalSort) return;
            //  Sort child lists
            for (const el of this.data) {
                if (!isObject(el) || !isNeArray(el.children)) continue;
                next([...el.children], sorted_list => el.children = sorted_list);
            }
            next([...this.data]);
        },
        sortSet: async function (lst) {
            Vue.set(this, 'sorted', [...lst]);
        },
        sortSelect: function (sort_data) {
            if (isNeString(this.pubkey)) {
                MeadCommon.Flash.publish(`${this.pubkey}:sort_select`, sort_data);
            }

            if(this.externalSort) {
                this.$emit('onSortSelected', sort_data);
            }
        },
        sort: async function () {
            await this.$nextTick();
            this.sorted = [...this.data];
            MeadCommon.Flash.publish(`${this.uid}:sort_run`, this.sorted);
        },
        /**
         * Called when a dropdown item is clicked on a child row
         */
        onDropdownItemClicked: function (data) {
            this.$emit('onDropdownItemClicked', data);
        },
        onCheckboxChange: function (data) {
            this.$emit('onCheckboxChange', data);
        },
        onButtonClicked: function (data) {
            this.$emit('onButtonClicked', data);
        },
        /**
         * Called when a row in the table is clicked
         * @param el - Object value for the row
         */
        onRowClicked: function (el) {
            if (!this.uiInteractive) return;
            this.$emit('onClick', el);
        },
        /**
         * Called when a particular column in the table is clicked
         * @param el - Object value for the row
         * @param column - Column key
         */
        onClickColumn: function (el, column) {
            if (!this.uiInteractive) return;
            this.$emit('onClickColumn', el, column);
        },
        /**
         * Called when the redirect arrow at the end of the row is clicked
         */
        onRedirect: function (el) {
            if (!this.uiInteractive) return;
            this.$emit('onRedirect', el);
        },
    },
    computed: {
        /**
         * If there is no data in the table
         */
        isEmpty: function () {
            return !isNeArray(this.data) && !isNeArray(this.pinnedData);
        },
        /**
         * Class names for each row
         */
        uiRowCname: function () {
            return [this.uiInteractive ? 'el--interactive' : ''];
        },
        /**
         * Columns that should be shown based on state of mobile view
         */
        columns: function () {
            const lst = [];
            for (const el of this.fields) {
                if (this.UIMobile && el.nomobile) continue;
                lst.push(el);
            }
            return lst;
        },
        /**
         * Super Headers that should be shown
         */
        uiSuperHeaders: function () {
            const list = [];
            for (const el of this.superHeaders) {
                if (this.UIMobile && el.nomobile) continue;
                list.push(el);
            }
            return list;
        },
        /**
         * If the table has super headers
         */
        hasSuperHeaders: function () {
            return this.uiSuperHeaders.length > 0;
        },
        /**
         * All rows for the table, computed after being sorted
         */
        rows: function () {
            const lst = [];
            for (const el of this.sorted) {
                const item = Object.assign(
                    {},
                    el,
                    ...[...this.columns.map(column => ({[`${column.key}_rendered`]: column.value(el)}))],
                    {uid: guuid()}
                );

                item.children = [];

                // Process children
                if (isNeArray(el.children)) {
                    for (const child of el.children) {
                        item.children.push(Object.assign(
                            {},
                            child,
                            ...[...this.columns.map(column => ({[`${column.key}_rendered`]: column.value(child)}))],
                            {uid: guuid()}
                        ));
                    }
                }

                lst.push(item);
            }
            return lst;
        },
        /**
         * Pinned rows for the table. Not sorted
         */
        rowsPinned: function () {
            const lst = [];
            for (const el of this.pinnedData) {
                lst.push(Object.assign(
                    {},
                    el,
                    ...[...this.columns.map(column => ({[`${column.key}_rendered`]: column.value(el)}))],
                    {uid: guuid()}
                ));
            }
            return lst;
        },
    },
    watch: {
        isLoading () {
            this.sort();
        },
        data () {
            this.sort();
        },
    },
    created () {
        this.sort();

        //  Listen to sort retrieve event
        MeadCommon.Flash.subscribe(this.uid, `${this.uid}:sort_retrieve`, this.sortRetrieve);

        //  Listen to sort set event
        MeadCommon.Flash.subscribe(this.uid, `${this.uid}:sort_set`, this.sortSet);

        //  Listen to sort select event
        MeadCommon.Flash.subscribe(this.uid, `${this.uid}:sort_select`, this.sortSelect);
    },
    destroyed () {
        MeadCommon.Flash.unsubscribe(this.uid);
    },
};
</script>
<style lang="scss">
@use '../../../../sass/variables' as *;

.ath-table-body-container {
    display: flex;
    flex-direction: column;
    overflow-x: scroll;

    &.restricted-height {
        overflow-y: auto;
        max-height: 60rem;
    }

    // Round top corners when restrictHeight is disabled because
    // overflow hidden is also removed.
    .round-top-corners {
        border-radius: 1rem 1rem 0 0;
    }

    // Round bottom corners when restrictHeight is disabled because
    // overflow hidden is also removed.
    .round-bottom-corners {
        border-radius: 0 0 1rem 1rem;

        // Make sure the background of the last row or rows with no children are also rounded
        .no-children,
        .last-row {
            border-radius: 0 0 1rem 1rem;
        }
    }

    .ath-table-body {
        // Ensures content (background color etc), expands into overflow area
        min-width: fit-content;
    }

    // Even / Odd color styling.
    .ath-row-even {
        background-color: var(--c-panel-bg);
    }

    .ath-row-odd {
        background-color: var(--c-panel-dark-bg);
    }

    // Cell Style
    .ath-table-cell {
        // This keeps our cells aligned on smaller mobile size widths
        min-width: 7rem;
    }

    // Default height for lazy rows so that they scroll correctly
    .ath-table-lazy {
        min-height: 5rem !important;
    }

    .ath-table-lock-first {
        .ath-lock-column {
            position: sticky;
            z-index: 2;

            &.ath-header-cell,
            &.ath-super-header-cell {
                // Covers other cells as the scroll under.
                // Headers always use dark color
                background-color: var(--c-panel-dark-bg);
            }

            &.ath-table-cell {
                // Covers other cells as the scroll under
                background-color: var(--c-panel-bg);
            }

            &.ath-left-lock-column {
                border-radius: $br-s 0 0 $br-s;

                // Add a border to the right of all locked cells that are
                // not the super headers (they will get their own right border).
                &:not(.ath-super-header-cell) {
                    border-right: 1px solid var(--c-panel-border);
                }
            }

            &.ath-right-lock-column {
                border-radius: 0 $br-s $br-s 0;

                // Add a border to the left of all locked cells that are
                // not the super headers (they will get their own border).
                &:not(.ath-super-header-cell) {
                    border-left: 1px solid var(--c-panel-border);
                }
            }
        }

        // Ensure locked row has the right background color
        // with even/odd colors. This rule needs to be below the
        // one above to overwrite it.
        .ath-row-even {
            .ath-lock-column {
               background-color: var(--c-panel-bg);
            }
        }

        .ath-row-odd {
            .ath-lock-column {
               background-color: var(--c-panel-dark-bg);
            }
        }

        // Apply border to locked column as well if set
        .ath-table-row.el--interactive {
            &:hover,
            &:active {
                .ath-lock-column {
                    box-shadow: inset 0 0 0 1px var(--c-panel-border-active);
                    transition: box-shadow 0.25s ease;
                    z-index: 3;
                }

                .ath-lock-column:not(:first-child) {
                    border-radius: 0;
                    box-shadow:
                        inset -1px 0 0 var(--c-panel-border-active), /* Right border */
                        inset 0 1px 0 var(--c-panel-border-active),  /* Top border */
                        inset 0 -1px 0 var(--c-panel-border-active); /* Bottom border */
                }
            }
        }
    }

    // Border for rows on hover
    .ath-table-row.el--interactive {
        &:hover,
        &:active {
            border-radius: $br-s;
            box-shadow: inset 0 0 0 1px var(--c-panel-border-active);
            transition: box-shadow 0.25s ease;
        }
    }

    //  Fix UI glitch between dropdown and fixed actions column
    .ath-lock-column:has(ul.el-dropdown) {
        z-index: 4 !important;
    }
}
</style>
